import React, { createContext, useContext, useEffect, useState } from "react";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

interface AnalyticsContextType {
  track: (event: string, properties?: Record<string, unknown>) => Promise<void>;
  page: (properties?: Record<string, unknown>) => Promise<void>;
  identify: (userId: string, traits?: Record<string, unknown>) => Promise<void>;
  setDataLayerVariable: (key: string, value: unknown) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
  track: async () => {},
  page: async () => {},
  identify: async () => {},
  setDataLayerVariable: () => {},
});

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      // Initialize Google Tag Manager inline script
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });

      const script = document.createElement("script");
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtm.js?id=GTM-MGBZ7NKD";

      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        // Fallback: append to head if no scripts exist
        document.head.appendChild(script);
      }

      setIsInitialized(true);
    }
  }, [isInitialized]);

  const getEventKey = (event: string, properties?: Record<string, unknown>) => {
    return `gtm_event_${event}_${JSON.stringify(properties || {})}`;
  };

  const hasEventBeenTracked = (eventKey: string) => {
    try {
      return localStorage.getItem(eventKey) === "true";
    } catch {
      return false;
    }
  };

  const markEventAsTracked = (eventKey: string) => {
    try {
      localStorage.setItem(eventKey, "true");
    } catch {
      // Fail silently if localStorage is not available
    }
  };

  const track = async (event: string, properties?: Record<string, unknown>) => {
    if (!window.dataLayer) {
      console.warn("GTM dataLayer not available for event:", event);
      return Promise.resolve(); // Resolve instead of reject
    }

    const eventKey = getEventKey(event, properties);
    if (hasEventBeenTracked(eventKey)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let isResolved = false;

      const eventCallback = () => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      };

      // Fire and forget - resolve after a short delay regardless of GTM response
      const gracefulTimeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      }, 500); // Much shorter timeout, just to ensure promise resolves

      const wrappedCallback = () => {
        clearTimeout(gracefulTimeout);
        eventCallback();
      };

      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event,
            ...properties,
            eventCallback: wrappedCallback,
          });
        } else {
          clearTimeout(gracefulTimeout);
          resolve(); // Still resolve even if dataLayer is unavailable
        }
      } catch (error) {
        clearTimeout(gracefulTimeout);
        console.warn("Failed to push event to GTM:", error);
        resolve(); // Always resolve for analytics
      }
    });
  };

  const page = async (properties?: Record<string, unknown>) => {
    if (!window.dataLayer) {
      console.warn("GTM dataLayer not available for page_view event");
      return Promise.resolve(); // Resolve instead of reject
    }

    const eventKey = getEventKey("page_view", properties);
    if (hasEventBeenTracked(eventKey)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let isResolved = false;

      const eventCallback = () => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      };

      // Fire and forget - resolve after a short delay regardless of GTM response
      const gracefulTimeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      }, 500); // Much shorter timeout, just to ensure promise resolves

      const wrappedCallback = () => {
        clearTimeout(gracefulTimeout);
        eventCallback();
      };

      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "page_view",
            ...properties,
            eventCallback: wrappedCallback,
          });
        } else {
          clearTimeout(gracefulTimeout);
          resolve(); // Still resolve even if dataLayer is unavailable
        }
      } catch (error) {
        clearTimeout(gracefulTimeout);
        console.warn("Failed to push page_view event to GTM:", error);
        resolve(); // Always resolve for analytics
      }
    });
  };

  const identify = async (userId: string, traits?: Record<string, unknown>) => {
    if (!window.dataLayer) {
      console.warn("GTM dataLayer not available for identify event");
      return Promise.resolve(); // Resolve instead of reject
    }

    const eventKey = getEventKey("identify", { user_id: userId, ...traits });
    if (hasEventBeenTracked(eventKey)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      let isResolved = false;

      const eventCallback = () => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      };

      // Fire and forget - resolve after a short delay regardless of GTM response
      const gracefulTimeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          markEventAsTracked(eventKey);
          resolve();
        }
      }, 500); // Much shorter timeout, just to ensure promise resolves

      const wrappedCallback = () => {
        clearTimeout(gracefulTimeout);
        eventCallback();
      };

      try {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: "identify",
            user_id: userId,
            ...traits,
            eventCallback: wrappedCallback,
          });
        } else {
          clearTimeout(gracefulTimeout);
          resolve(); // Still resolve even if dataLayer is unavailable
        }
      } catch (error) {
        clearTimeout(gracefulTimeout);
        console.warn("Failed to push identify event to GTM:", error);
        resolve(); // Always resolve for analytics
      }
    });
  };

  const setDataLayerVariable = (key: string, value: unknown) => {
    if (!window.dataLayer) {
      console.warn("GTM dataLayer not available for setting variable:", key);
      return;
    }

    try {
      window.dataLayer.push({ [key]: value });
    } catch (error) {
      console.warn("Failed to set GTM dataLayer variable:", error);
    }
  };

  return (
    <AnalyticsContext.Provider
      value={{ track, page, identify, setDataLayerVariable }}
    >
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-MGBZ7NKD"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="Google Tag Manager"
        />
      </noscript>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
